import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import Sidebar from '../components/Sidebar';
import CustomerClusterViewer from '../components/CustomerClusterViewer';

const CustomerDashboard = () => {
    const { user } = useAuth();
    const [dashboardData, setDashboardData] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('sybil');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch contracts and dashboard data
            const [contractsResult, dashboardResult] = await Promise.all([
                authService.getUserContracts(),
                authService.getCustomerDashboard()
            ]);

            if (contractsResult.ok) {
                setContracts(contractsResult.data);
            }

            if (dashboardResult.ok) {
                setDashboardData(dashboardResult.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('An error occurred while loading your dashboard');
        } finally {
            setLoading(false);
        }
    };

    const formatAddress = (addr) => {
        if (!addr || addr.length < 10) return addr;
        return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    };

    const formatNumber = (num) => {
        return num?.toLocaleString() || '0';
    };

    if (loading) {
        return (
            <div className="d-flex bg-dark text-light" style={{ minHeight: '100vh' }}>
                <div className="container-fluid py-4 px-4">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-secondary">Loading your dashboard...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !dashboardData) {
        return (
            <div className="d-flex bg-dark text-light" style={{ minHeight: '100vh' }}>
                <div className="container-fluid py-4 px-4">
                    <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error || 'No contract assigned yet. Please contact support.'}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex bg-dark text-light" style={{ minHeight: '100vh' }}>
            {/* Sidebar */}
            <Sidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isCustomer={true}
            />

            {/* Main Content Area */}
            <div
                className="flex-grow-1"
                style={{
                    marginLeft: sidebarCollapsed ? '80px' : '260px',
                    transition: 'margin-left 0.3s ease',
                    minHeight: '100vh',
                    backgroundColor: '#0a0a0f'
                }}
            >
                {/* Top Header Bar */}
                <div className="bg-black border-bottom border-secondary sticky-top" style={{ zIndex: 100 }}>
                    <div className="container-fluid py-3 px-4">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <h4 className="mb-0 text-white">
                                    {activeTab === 'sybil' && (
                                        <>
                                            <i className="bi bi-shield-exclamation text-danger me-2"></i>
                                            Sybil Detection
                                        </>
                                    )}
                                    {activeTab === 'graph' && (
                                        <>
                                            <i className="bi bi-diagram-3 text-success me-2"></i>
                                            Sybil Clusters
                                        </>
                                    )}
                                    {activeTab === 'api' && (
                                        <>
                                            <i className="bi bi-code-square text-info me-2"></i>
                                            API & Documentation
                                        </>
                                    )}
                                </h4>
                                <p className="text-secondary small mb-0">
                                    {activeTab === 'sybil' && 'Real-time Sybil detection for your contract'}
                                    {activeTab === 'graph' && 'Visualize Sybil clusters and network activity'}
                                    {activeTab === 'api' && 'API access and documentation for your contract'}
                                </p>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {user?.org && (
                                    <span className="badge bg-info text-dark">
                                        {user.org}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                {activeTab === 'sybil' && (
                    <SybilDashboardContent dashboardData={dashboardData} formatNumber={formatNumber} formatAddress={formatAddress} />
                )}
                {activeTab === 'graph' && (
                    <GraphDashboardContent dashboardData={dashboardData} />
                )}
                {activeTab === 'api' && (
                    <ApiDashboardContent dashboardData={dashboardData} user={user} />
                )}
            </div>
        </div>
    );
};

// Sybil Dashboard Content Component
const SybilDashboardContent = ({ dashboardData, formatNumber, formatAddress }) => {
    return (
        <div className="container-fluid my-4 px-4">
            {/* Monitored Contract */}
            <div className="card bg-secondary-subtle text-light mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">
                        <i className="bi bi-file-earmark-code text-info me-2"></i>
                        Your Monitored Contract
                    </h5>
                    <div className="bg-dark p-3 rounded">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <code className="text-warning" style={{ fontSize: '1.1rem' }}>
                                    {dashboardData.contract}
                                </code>
                                <p className="text-secondary small mb-0 mt-2">
                                    Chain: {dashboardData.chain_id}
                                </p>
                            </div>
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigator.clipboard.writeText(dashboardData.contract)}
                            >
                                <i className="bi bi-clipboard"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card bg-secondary-subtle text-light">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-secondary mb-1">Sybil Wallets</h6>
                                    <h3 className="mb-0 text-danger">{formatNumber(dashboardData.total_wallets)}</h3>
                                </div>
                                <i className="bi bi-wallet2 text-danger" style={{ fontSize: '2rem' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card bg-secondary-subtle text-light">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-secondary mb-1">Sybil Clusters</h6>
                                    <h3 className="mb-0 text-warning">{formatNumber(dashboardData.total_clusters)}</h3>
                                </div>
                                <i className="bi bi-diagram-3 text-warning" style={{ fontSize: '2rem' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card bg-secondary-subtle text-light">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-secondary mb-1">Total Interactions</h6>
                                    <h3 className="mb-0 text-info">{formatNumber(dashboardData.total_calls)}</h3>
                                </div>
                                <i className="bi bi-arrow-left-right text-info" style={{ fontSize: '2rem' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card bg-secondary-subtle text-light">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="text-secondary mb-1">Activity Period</h6>
                                    <p className="mb-0 small text-light">
                                        {dashboardData.first_seen ? new Date(dashboardData.first_seen).toLocaleDateString() : '-'}
                                        {' → '}
                                        {dashboardData.last_seen ? new Date(dashboardData.last_seen).toLocaleDateString() : '-'}
                                    </p>
                                </div>
                                <i className="bi bi-calendar-range text-success" style={{ fontSize: '2rem' }}></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Detected Typologies */}
            {dashboardData.typologies && dashboardData.typologies.length > 0 && (
                <div className="card bg-secondary-subtle text-light mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">
                            <i className="bi bi-tags text-warning me-2"></i>
                            Detected Attack Typologies
                        </h5>
                        <div className="d-flex flex-wrap gap-2">
                            {dashboardData.typologies.map((typ, idx) => (
                                <span key={idx} className="badge bg-danger fs-6 px-3 py-2">
                                    {typ}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Info Card */}
            <div className="card bg-secondary-subtle text-light">
                <div className="card-body">
                    <h5 className="card-title mb-3">
                        <i className="bi bi-info-circle text-primary me-2"></i>
                        About Your Dashboard
                    </h5>
                    <p className="text-secondary mb-2">
                        This dashboard provides real-time Sybil detection data specifically for your smart contract.
                    </p>
                    <ul className="text-secondary mb-0">
                        <li><strong>Sybil Wallets:</strong> Unique wallet addresses identified as part of Sybil clusters targeting your contract</li>
                        <li><strong>Sybil Clusters:</strong> Groups of coordinated wallets detected interacting with your contract</li>
                        <li><strong>Total Interactions:</strong> Number of suspicious transactions detected</li>
                        <li>Explore individual Sybil clusters in the "Sybil Clusters" tab</li>
                        <li>Access your API key and documentation in the "API & Documentation" tab</li>
                        <li>Data is updated continuously as new transactions are detected</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// Graph Dashboard Content Component
const GraphDashboardContent = ({ dashboardData }) => {
    return (
        <div className="container-fluid my-4 px-4">
            {/* Contract Info Header */}
            <div className="card bg-secondary-subtle text-light mb-4">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="card-title mb-1">
                                <i className="bi bi-diagram-3 text-success me-2"></i>
                                Sybil Clusters for Your Contract
                            </h5>
                            <p className="text-secondary small mb-0">
                                Viewing Sybil clusters that have interacted with: <code className="text-warning">{dashboardData.contract}</code>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cluster Viewer */}
            <CustomerClusterViewer contractAddress={dashboardData.contract} />
        </div>
    );
};

// API Dashboard Content Component
const ApiDashboardContent = ({ dashboardData, user }) => {
    const [expandedEndpoints, setExpandedEndpoints] = useState({ dashboard: true }); // First one expanded by default
    const [endpointResponses, setEndpointResponses] = useState({}); // Store responses per endpoint
    const [endpointLoading, setEndpointLoading] = useState({});
    const [endpointErrors, setEndpointErrors] = useState({});
    const [endpointTimes, setEndpointTimes] = useState({});

    // API Key management state
    const [apiKeys, setApiKeys] = useState([]);
    const [keysLoading, setKeysLoading] = useState(true);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyExpiry, setNewKeyExpiry] = useState('');
    const [creatingKey, setCreatingKey] = useState(false);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [keyError, setKeyError] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);

    const baseUrl = window.location.origin;
    const contractAddress = dashboardData?.contract || '';

    // Toggle accordion expansion
    const toggleEndpoint = (endpointId) => {
        setExpandedEndpoints(prev => ({
            ...prev,
            [endpointId]: !prev[endpointId]
        }));
    };

    // Fetch API keys on mount
    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        try {
            setKeysLoading(true);
            const response = await fetch('/api/auth/api-keys', {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setApiKeys(data);
            }
        } catch (err) {
            console.error('Failed to fetch API keys:', err);
        } finally {
            setKeysLoading(false);
        }
    };

    const createApiKey = async () => {
        if (!newKeyName.trim()) {
            setKeyError('Please enter a name for your API key');
            return;
        }

        setCreatingKey(true);
        setKeyError(null);
        setNewlyCreatedKey(null);

        try {
            const body = { name: newKeyName.trim() };
            if (newKeyExpiry) {
                body.expires_in_days = parseInt(newKeyExpiry);
            }

            const response = await fetch('/api/auth/api-keys', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                setNewlyCreatedKey(data);
                setNewKeyName('');
                setNewKeyExpiry('');
                setShowCreateForm(false);
                fetchApiKeys(); // Refresh the list
            } else {
                const text = await response.text();
                setKeyError(text || 'Failed to create API key');
            }
        } catch (err) {
            setKeyError('Failed to create API key');
        } finally {
            setCreatingKey(false);
        }
    };

    const revokeApiKey = async (keyId) => {
        if (!window.confirm('Are you sure you want to revoke this API key? It will immediately stop working.')) {
            return;
        }

        try {
            const response = await fetch(`/api/auth/api-keys/${keyId}/revoke`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                fetchApiKeys();
            }
        } catch (err) {
            console.error('Failed to revoke API key:', err);
        }
    };

    const deleteApiKey = async (keyId) => {
        if (!window.confirm('Are you sure you want to permanently delete this API key?')) {
            return;
        }

        try {
            const response = await fetch(`/api/auth/api-keys/${keyId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (response.ok) {
                fetchApiKeys();
            }
        } catch (err) {
            console.error('Failed to delete API key:', err);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleString();
    };

    const endpoints = [
        {
            id: 'dashboard',
            method: 'GET',
            path: '/api/v1/customer/dashboard',
            name: 'Dashboard Overview',
            description: 'Get aggregated Sybil detection statistics for your contract including total wallets, clusters, and detected typologies.',
            responseFields: [
                { name: 'contract', type: 'string', desc: 'Your monitored contract address' },
                { name: 'total_wallets', type: 'number', desc: 'Total unique Sybil wallets detected' },
                { name: 'total_clusters', type: 'number', desc: 'Number of Sybil clusters identified' },
                { name: 'total_calls', type: 'number', desc: 'Total suspicious interactions' },
                { name: 'typologies', type: 'array', desc: 'Attack types detected (e.g., Wash Trading, Asset Farming)' },
                { name: 'first_seen', type: 'datetime', desc: 'First detection timestamp' },
                { name: 'last_seen', type: 'datetime', desc: 'Most recent detection timestamp' },
            ]
        },
        {
            id: 'stats',
            method: 'GET',
            path: `/api/v1/customer/sybil/stats/${contractAddress}`,
            name: 'Sybil Statistics',
            description: 'Get detailed Sybil statistics for your specific contract address.',
            responseFields: [
                { name: 'contract', type: 'string', desc: 'Contract address' },
                { name: 'total_wallets', type: 'number', desc: 'Total unique Sybil wallets' },
                { name: 'total_clusters', type: 'number', desc: 'Number of clusters' },
                { name: 'total_interactions', type: 'number', desc: 'Total suspicious transactions' },
                { name: 'typologies', type: 'array', desc: 'Detected attack types' },
            ]
        },
        {
            id: 'activity',
            method: 'GET',
            path: `/api/v1/customer/sybil/activity/${contractAddress}`,
            name: 'Recent Activity',
            description: 'Get the 100 most recent Sybil detection events for your contract.',
            responseFields: [
                { name: 'activities', type: 'array', desc: 'List of recent Sybil detections' },
                { name: 'activities[].wallet', type: 'string', desc: 'Detected Sybil wallet address' },
                { name: 'activities[].cluster_id', type: 'string', desc: 'Cluster identifier' },
                { name: 'activities[].typology', type: 'string', desc: 'Attack type classification' },
                { name: 'activities[].detected_at', type: 'datetime', desc: 'Detection timestamp' },
            ]
        },
        {
            id: 'clusters',
            method: 'GET',
            path: `/api/v1/customer/sybil/clusters/${contractAddress}`,
            name: 'Sybil Clusters',
            description: 'Get a list of Sybil clusters that have interacted with your contract, ordered by wallet count.',
            responseFields: [
                { name: 'clusters', type: 'array', desc: 'List of Sybil clusters' },
                { name: 'clusters[].cluster_id', type: 'string', desc: 'Unique cluster identifier' },
                { name: 'clusters[].wallet_count', type: 'number', desc: 'Number of wallets in cluster' },
                { name: 'clusters[].typology', type: 'string', desc: 'Primary attack type' },
                { name: 'total_clusters', type: 'number', desc: 'Total cluster count' },
            ]
        },
        {
            id: 'wallets',
            method: 'GET',
            path: `/api/v1/customer/sybil/wallets/${contractAddress}`,
            name: 'All Sybil Wallets',
            description: 'Get all Sybil wallet addresses grouped by cluster. Returns every detected wallet for your contract.',
            responseFields: [
                { name: 'clusters', type: 'array', desc: 'List of clusters with their wallets' },
                { name: 'clusters[].cluster_id', type: 'string', desc: 'Unique cluster identifier' },
                { name: 'clusters[].wallets', type: 'array', desc: 'Array of wallet addresses in cluster' },
                { name: 'clusters[].wallet_count', type: 'number', desc: 'Number of wallets in cluster' },
                { name: 'clusters[].typology', type: 'string', desc: 'Primary attack type' },
                { name: 'clusters[].interaction_count', type: 'number', desc: 'Total interactions from cluster' },
                { name: 'total_clusters', type: 'number', desc: 'Total cluster count' },
                { name: 'total_wallets', type: 'number', desc: 'Total wallet count across all clusters' },
            ]
        }
    ];

    const testEndpoint = async (endpoint) => {
        setEndpointLoading(prev => ({ ...prev, [endpoint.id]: true }));
        setEndpointErrors(prev => ({ ...prev, [endpoint.id]: null }));
        setEndpointResponses(prev => ({ ...prev, [endpoint.id]: null }));

        const startTime = performance.now();

        try {
            const response = await fetch(endpoint.path, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });

            const endTime = performance.now();
            setEndpointTimes(prev => ({ ...prev, [endpoint.id]: Math.round(endTime - startTime) }));

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            setEndpointResponses(prev => ({ ...prev, [endpoint.id]: data }));
        } catch (err) {
            setEndpointErrors(prev => ({ ...prev, [endpoint.id]: err.message }));
        } finally {
            setEndpointLoading(prev => ({ ...prev, [endpoint.id]: false }));
        }
    };

    // Limit array fields for display - show max 5 items with truncation notice
    const limitResponseForDisplay = (data, endpointId) => {
        if (!data) return data;

        const clonedData = JSON.parse(JSON.stringify(data));
        const MAX_ITEMS = 5;
        const MAX_WALLETS_PER_CLUSTER = 10;

        // Handle different endpoint response structures
        if (endpointId === 'activity' && clonedData.activities && Array.isArray(clonedData.activities)) {
            if (clonedData.activities.length > MAX_ITEMS) {
                clonedData.activities = clonedData.activities.slice(0, MAX_ITEMS);
                clonedData._truncated = true;
                clonedData._truncated_note = `Showing ${MAX_ITEMS} of ${data.total_count || data.activities.length} activities`;
            }
        }

        if (endpointId === 'clusters' && clonedData.clusters && Array.isArray(clonedData.clusters)) {
            if (clonedData.clusters.length > MAX_ITEMS) {
                clonedData.clusters = clonedData.clusters.slice(0, MAX_ITEMS);
                clonedData._truncated = true;
                clonedData._truncated_note = `Showing ${MAX_ITEMS} of ${data.total_clusters || data.clusters.length} clusters`;
            }
        }

        // Handle wallets endpoint - limit clusters and wallets per cluster for display
        if (endpointId === 'wallets' && clonedData.clusters && Array.isArray(clonedData.clusters)) {
            let walletsShown = 0;
            const originalTotalWallets = data.total_wallets || 0;

            // Limit to 3 clusters for display
            if (clonedData.clusters.length > 3) {
                clonedData.clusters = clonedData.clusters.slice(0, 3);
            }

            // Limit wallets per cluster
            clonedData.clusters = clonedData.clusters.map(cluster => {
                const originalCount = cluster.wallets?.length || 0;
                if (cluster.wallets && cluster.wallets.length > MAX_WALLETS_PER_CLUSTER) {
                    cluster.wallets = cluster.wallets.slice(0, MAX_WALLETS_PER_CLUSTER);
                    cluster._wallets_truncated = `Showing ${MAX_WALLETS_PER_CLUSTER} of ${originalCount} wallets`;
                }
                walletsShown += cluster.wallets?.length || 0;
                return cluster;
            });

            if (data.clusters.length > 3 || originalTotalWallets > walletsShown) {
                clonedData._truncated = true;
                clonedData._truncated_note = `Showing ${walletsShown} wallets from 3 clusters — Full data (${originalTotalWallets} wallets, ${data.total_clusters} clusters) available via API`;
            }
        }

        if (endpointId === 'dashboard' && clonedData.typologies && Array.isArray(clonedData.typologies)) {
            if (clonedData.typologies.length > 6) {
                clonedData.typologies = clonedData.typologies.slice(0, 6);
                clonedData._truncated_typologies = true;
            }
        }

        if (endpointId === 'dashboard' && clonedData.all_contracts && Array.isArray(clonedData.all_contracts)) {
            if (clonedData.all_contracts.length > 3) {
                clonedData.all_contracts = clonedData.all_contracts.slice(0, 3);
                clonedData._truncated_contracts = true;
            }
        }

        return clonedData;
    };

    const formatJson = (obj) => {
        return JSON.stringify(obj, null, 2);
    };

    return (
        <div className="container my-4">
            {/* Quick Start Card */}
            <div className="card bg-gradient text-light mb-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h4 className="mb-2">
                                <i className="bi bi-lightning-charge-fill text-warning me-2"></i>
                                Sybil Detection API
                            </h4>
                            <p className="text-secondary mb-0">
                                Access real-time Sybil detection data for your contract via our REST API.
                                All endpoints use session-based authentication.
                            </p>
                        </div>
                        <div className="col-md-4 text-md-end mt-3 mt-md-0">
                            <span className="badge bg-success fs-6 px-3 py-2">
                                <i className="bi bi-check-circle me-1"></i>
                                API Active
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Base URL & Auth */}
            <div className="row g-4 mb-4">
                <div className="col-md-6">
                    <div className="card bg-secondary-subtle text-light h-100">
                        <div className="card-body">
                            <h6 className="text-secondary mb-2">
                                <i className="bi bi-globe me-2"></i>Base URL
                            </h6>
                            <code className="d-block bg-dark p-3 rounded text-success fs-6">
                                {baseUrl}/api
                            </code>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card bg-secondary-subtle text-light h-100">
                        <div className="card-body">
                            <h6 className="text-secondary mb-2">
                                <i className="bi bi-shield-lock me-2"></i>Authentication
                            </h6>
                            <p className="small text-light mb-2">Use API keys for programmatic access</p>
                            <code className="d-block bg-dark p-2 rounded text-info small">
                                Authorization: Bearer fl_live_xxxxx...
                            </code>
                        </div>
                    </div>
                </div>
            </div>

            {/* API Keys Management */}
            <div className="card bg-secondary-subtle text-light mb-4">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                            <i className="bi bi-key-fill text-warning me-2"></i>
                            API Keys
                        </h5>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowCreateForm(!showCreateForm)}
                        >
                            <i className="bi bi-plus-lg me-1"></i>
                            Create New Key
                        </button>
                    </div>

                    {/* Create New Key Form */}
                    {showCreateForm && (
                        <div className="bg-dark p-3 rounded mb-3">
                            <h6 className="text-light mb-3">Create New API Key</h6>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label small text-secondary">Key Name *</label>
                                    <input
                                        type="text"
                                        className="form-control bg-secondary-subtle text-light border-secondary"
                                        placeholder="e.g., Production Server"
                                        value={newKeyName}
                                        onChange={(e) => setNewKeyName(e.target.value)}
                                        maxLength={100}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small text-secondary">Expires In (days)</label>
                                    <select
                                        className="form-select bg-secondary-subtle text-light border-secondary"
                                        value={newKeyExpiry}
                                        onChange={(e) => setNewKeyExpiry(e.target.value)}
                                    >
                                        <option value="">Never expires</option>
                                        <option value="30">30 days</option>
                                        <option value="90">90 days</option>
                                        <option value="180">180 days</option>
                                        <option value="365">1 year</option>
                                    </select>
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <button
                                        className="btn btn-success w-100"
                                        onClick={createApiKey}
                                        disabled={creatingKey}
                                    >
                                        {creatingKey ? (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        ) : (
                                            'Create'
                                        )}
                                    </button>
                                </div>
                            </div>
                            {keyError && (
                                <div className="alert alert-danger mt-3 mb-0 py-2 small">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {keyError}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Newly Created Key Alert */}
                    {newlyCreatedKey && (
                        <div className="alert alert-success mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <strong>
                                    <i className="bi bi-check-circle me-2"></i>
                                    API Key Created: {newlyCreatedKey.name}
                                </strong>
                                <button
                                    className="btn btn-sm btn-outline-light"
                                    onClick={() => setNewlyCreatedKey(null)}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                            <div className="bg-dark p-3 rounded mb-2">
                                <code className="text-warning user-select-all" style={{ wordBreak: 'break-all' }}>
                                    {newlyCreatedKey.key}
                                </code>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-danger fw-bold">
                                    <i className="bi bi-exclamation-diamond me-1"></i>
                                    Copy this key now! It will not be shown again.
                                </small>
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => copyToClipboard(newlyCreatedKey.key)}
                                >
                                    <i className="bi bi-clipboard me-1"></i>
                                    Copy
                                </button>
                            </div>
                        </div>
                    )}

                    {/* API Keys List */}
                    {keysLoading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border spinner-border-sm text-secondary"></div>
                            <span className="ms-2 text-secondary">Loading keys...</span>
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <div className="text-center py-4 text-secondary">
                            <i className="bi bi-key fs-1 mb-2 d-block opacity-50"></i>
                            <p className="mb-0">No API keys yet. Create one to get started.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-dark table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Key</th>
                                        <th>Created</th>
                                        <th>Last Used</th>
                                        <th>Expires</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {apiKeys.map((key) => (
                                        <tr key={key.id} className={key.revoked ? 'opacity-50' : ''}>
                                            <td className="fw-medium">{key.name}</td>
                                            <td>
                                                <code className="text-info">{key.key_prefix}...</code>
                                            </td>
                                            <td className="small text-secondary">
                                                {formatDate(key.created_at)}
                                            </td>
                                            <td className="small text-secondary">
                                                {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                                            </td>
                                            <td className="small text-secondary">
                                                {key.expires_at ? formatDate(key.expires_at) : 'Never'}
                                            </td>
                                            <td>
                                                {key.is_active ? (
                                                    <span className="badge bg-success">Active</span>
                                                ) : key.revoked ? (
                                                    <span className="badge bg-danger">Revoked</span>
                                                ) : (
                                                    <span className="badge bg-warning text-dark">Expired</span>
                                                )}
                                            </td>
                                            <td className="text-end">
                                                {key.is_active && (
                                                    <button
                                                        className="btn btn-sm btn-outline-warning me-1"
                                                        onClick={() => revokeApiKey(key.id)}
                                                        title="Revoke key"
                                                    >
                                                        <i className="bi bi-slash-circle"></i>
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => deleteApiKey(key.id)}
                                                    title="Delete key"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="mt-3 small text-secondary">
                        <i className="bi bi-info-circle me-1"></i>
                        API keys are securely hashed. Lost keys cannot be recovered - create a new one if needed.
                    </div>
                </div>
            </div>

            {/* Your Contract */}
            <div className="card bg-secondary-subtle text-light mb-4">
                <div className="card-body">
                    <h6 className="text-secondary mb-2">
                        <i className="bi bi-file-earmark-code me-2"></i>Your Monitored Contract
                    </h6>
                    <code className="d-block bg-dark p-3 rounded text-warning fs-6">
                        {contractAddress}
                    </code>
                    <p className="text-secondary small mt-2 mb-0">
                        Chain: <span className="text-light">{dashboardData?.chain_id || 'eip155:1'}</span>
                    </p>
                </div>
            </div>

            {/* API Endpoints */}
            <h5 className="mb-3">
                <i className="bi bi-plug text-info me-2"></i>
                Available Endpoints
            </h5>

            <div className="accordion" id="apiEndpoints">
                {endpoints.map((endpoint) => {
                    const isExpanded = expandedEndpoints[endpoint.id] || false;
                    const isLoading = endpointLoading[endpoint.id] || false;
                    const response = endpointResponses[endpoint.id];
                    const error = endpointErrors[endpoint.id];
                    const responseTime = endpointTimes[endpoint.id];
                    const displayResponse = limitResponseForDisplay(response, endpoint.id);

                    return (
                        <div className="accordion-item bg-secondary-subtle border-secondary" key={endpoint.id}>
                            <h2 className="accordion-header">
                                <button
                                    className={`accordion-button bg-secondary-subtle text-light ${isExpanded ? '' : 'collapsed'}`}
                                    type="button"
                                    onClick={() => toggleEndpoint(endpoint.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="badge bg-success me-3">{endpoint.method}</span>
                                    <code className="text-warning">{endpoint.path.replace(contractAddress, '{contract}')}</code>
                                    <span className="ms-auto me-3 text-secondary small">{endpoint.name}</span>
                                    <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2`}></i>
                                </button>
                            </h2>
                            <div
                                className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}
                                style={{
                                    display: isExpanded ? 'block' : 'none',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                            >
                                <div className="accordion-body bg-dark">
                                    <p className="text-secondary">{endpoint.description}</p>

                                    {/* Try It Button */}
                                    <div className="mb-3">
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => testEndpoint(endpoint)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Testing...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-play-fill me-2"></i>
                                                    Try It Live
                                                </>
                                            )}
                                        </button>
                                        {responseTime && !isLoading && (
                                            <span className="ms-3 badge bg-secondary">
                                                <i className="bi bi-clock me-1"></i>
                                                {responseTime}ms
                                            </span>
                                        )}
                                    </div>

                                    {/* Response Fields */}
                                    <h6 className="text-secondary mb-2">Response Fields</h6>
                                    <table className="table table-dark table-sm small">
                                        <thead>
                                            <tr>
                                                <th>Field</th>
                                                <th>Type</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {endpoint.responseFields.map((field, i) => (
                                                <tr key={i}>
                                                    <td><code className="text-warning">{field.name}</code></td>
                                                    <td><span className="text-info">{field.type}</span></td>
                                                    <td className="text-secondary">{field.desc}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Live Response */}
                                    {(response || error) && (
                                        <div className="mt-3">
                                            <h6 className="text-secondary mb-2">
                                                <i className="bi bi-terminal me-2"></i>
                                                Live Response
                                                {responseTime && (
                                                    <span className="badge bg-success ms-2">{responseTime}ms</span>
                                                )}
                                            </h6>
                                            {error ? (
                                                <div className="alert alert-danger small">
                                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                                    {error}
                                                </div>
                                            ) : (
                                                <>
                                                    {displayResponse?._truncated && (
                                                        <div className="alert alert-info small py-2 mb-2">
                                                            <i className="bi bi-info-circle me-2"></i>
                                                            {displayResponse._truncated_note} — Full data available via API
                                                        </div>
                                                    )}
                                                    <pre className="bg-black p-3 rounded small" style={{ maxHeight: '350px', overflow: 'auto' }}>
                                                        <code className="text-success">{formatJson(displayResponse)}</code>
                                                    </pre>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* cURL Example */}
                                    <div className="mt-3">
                                        <h6 className="text-secondary mb-2">
                                            <i className="bi bi-code-slash me-2"></i>
                                            cURL Example
                                        </h6>
                                        <pre className="bg-black p-3 rounded small">
                                            <code className="text-success">{`curl "${baseUrl}${endpoint.path}" \\
  -H "Cookie: session=YOUR_SESSION_TOKEN"`}</code>
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Rate Limits & HTTP Codes */}
            <div className="row g-4 mt-3">
                <div className="col-md-6">
                    <div className="card bg-secondary-subtle text-light h-100">
                        <div className="card-body">
                            <h6 className="mb-3">
                                <i className="bi bi-speedometer2 text-warning me-2"></i>
                                Rate Limits
                            </h6>
                            <ul className="list-unstyled mb-0 small">
                                <li className="mb-2">
                                    <span className="badge bg-primary me-2">1000</span>
                                    requests per hour
                                </li>
                                <li>
                                    <span className="badge bg-info me-2">100</span>
                                    requests per minute
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card bg-secondary-subtle text-light h-100">
                        <div className="card-body">
                            <h6 className="mb-3">
                                <i className="bi bi-info-circle text-info me-2"></i>
                                HTTP Status Codes
                            </h6>
                            <ul className="list-unstyled mb-0 small">
                                <li className="mb-1"><code className="text-success">200</code> Success</li>
                                <li className="mb-1"><code className="text-warning">401</code> Unauthorized</li>
                                <li className="mb-1"><code className="text-danger">403</code> Access Denied</li>
                                <li><code className="text-danger">429</code> Rate Limited</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Support Card */}
            <div className="card bg-secondary-subtle text-light mt-4">
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h5 className="mb-2">
                                <i className="bi bi-headset text-primary me-2"></i>
                                Need Help?
                            </h5>
                            <p className="text-secondary mb-md-0">
                                Our team is here to help you integrate Sybil detection into your application.
                            </p>
                        </div>
                        <div className="col-md-4 text-md-end mt-3 mt-md-0">
                            <a href="mailto:support@frwdlabs.xyz" className="btn btn-outline-primary">
                                <i className="bi bi-envelope me-2"></i>
                                Contact Support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerDashboard;
